import React, { useEffect, useRef, useState } from "react";
import { StyleSheet, View, ActivityIndicator, Animated } from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { ThemedText } from "../ThemedText";
import { ThemedPlatformSymbol } from "../ThemedPlatformSymbol";
import { useThemeColor } from "@/hooks/useThemeColor";

/**
 * Props for the AutoSaveIndicator component.
 */
export interface IAutoSaveIndicatorProps {
  /** Current auto-save status. */
  status: "idle" | "saving" | "saved" | "error";
  /** Optional custom error message. */
  errorMessage?: string | null;
  /** Custom label when saved. Default is "Saved". */
  savedLabel?: string;
}

/**
 * Visual badge indicating auto-save status (saving, saved, error) with smooth fade transitions.
 */
export function AutoSaveIndicator({
  status,
  errorMessage,
  savedLabel = "Saved",
}: IAutoSaveIndicatorProps): React.JSX.Element | null {
  const tintColor = useThemeColor({}, "tint");
  const borderColor = useThemeColor({}, "border");
  const opacityAnim = useRef(new Animated.Value(status === "idle" ? 0 : 1)).current;
  const [visible, setVisible] = useState(status !== "idle");
  const lastActiveStatus = useRef(status);

  if (status !== "idle") {
    lastActiveStatus.current = status;
  }

  useEffect(() => {
    if (status !== "idle") {
      setVisible(true);
      Animated.timing(opacityAnim, {
        toValue: 1,
        duration: 180,
        useNativeDriver: true,
      }).start();
    } else {
      Animated.timing(opacityAnim, {
        toValue: 0,
        duration: 250,
        useNativeDriver: true,
      }).start(({ finished }) => {
        if (finished) {
          setVisible(false);
        }
      });
    }
  }, [status, opacityAnim]);

  if (!visible && status === "idle") {
    return null;
  }

  const activeStatus = status === "idle" ? lastActiveStatus.current : status;

  return (
    <Animated.View style={[styles.container, { borderColor, opacity: opacityAnim }]} testID="auto-save-indicator">
      {activeStatus === "saving" && (
        <>
          <ActivityIndicator size="small" color={tintColor} style={styles.spinner} />
          <ThemedText style={styles.statusText}>Saving changes...</ThemedText>
        </>
      )}

      {activeStatus === "saved" && (
        <ThemedPlatformSymbol type="saved" size="small" />
      )}

      {activeStatus === "error" && (
        <>
          <Ionicons name="alert-circle-outline" size={14} color="#EF4444" />
          <ThemedText style={[styles.statusText, { color: "#EF4444" }]}>
            {errorMessage || "Save failed"}
          </ThemedText>
        </>
      )}
    </Animated.View>
  );
}

const styles = StyleSheet.create({
  container: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 0,
    borderWidth: 1,
    gap: 5,
    backgroundColor: "rgba(150, 150, 150, 0.05)",
  },
  spinner: {
    transform: [{ scale: 0.7 }],
  },
  statusText: {
    fontSize: 11,
    fontWeight: "600",
  },
});
